<template>
  <div>
    <div class="content">
      <LoginLogo/>

      <v-container v-if="isStripeSubscription">
        <!-- eslint-disable-next-line -->
        <v-row v-html="$t('routes.bandwidthExceeded.subscriptionBandwidthExceeded', {
          contactEmail: 'accounts@tubeway.io'
        })"/>

        <v-row v-if="$canControlTenantConfig()">
          {{ $t('routes.bandwidthExceeded.stripeBuyAdditionalCredits') }}
        </v-row>

        <v-row v-if="$canControlTenantConfig()">
          <v-btn
            :href="stripeurl"
            :disabled="!stripeurl"
            color="secondary"
            target="_blank"
            block
          >
            Go to website
            <v-icon size="small">mdi-arrow-top-right-bold-box-outline</v-icon>
          </v-btn>

          <div v-if="errorMessage" class="bg-error" data-cy="">
            {{ errorMessage }}
          </div>
        </v-row>

        <v-row v-if="!isLogoutHidden" class="mt-5">
          <v-btn type="button" @click="logout" class="bg-primary" block>
            {{ $t('layouts.main.logout') }}
          </v-btn>
        </v-row>
      </v-container>
      <v-container v-else-if="!isRecurringSubscription">
        <!-- eslint-disable-next-line -->
        <v-row v-html="$t('routes.bandwidthExceeded.subscriptionBandwidthExceeded', {
          contactEmail: 'accounts@tubeway.io'
        })"/>

        <v-row v-if="!isLogoutHidden" class="mt-5">
          <v-btn type="button" @click="logout" class="bg-primary" block>
            {{ $t('layouts.main.logout') }}
          </v-btn>
        </v-row>
      </v-container>
      <v-container v-else-if="tenantLimits">
        <v-row>
          <span>{{ $t('routes.bandwidthExceeded.enterpriseMonthlyBandwidthExceeded') }}</span>
        </v-row>

        <v-row v-if="!$canControlTenantConfig()">
          {{ $t('routes.bandwidthExceeded.enterpriseContactTenantAdministrator') }}
        </v-row>
        <v-row v-else>
          <span>{{ $t('routes.bandwidthExceeded.enterpriseMonthlyBandwidthUsage', {
            current: formatBytesAsGigabytes(bandwidthLimits.spent),
            limit: formatBytesAsGigabytes(bandwidthLimits.limit)
          }) }}</span>
          <span>{{ $t('routes.bandwidthExceeded.enterpriseResetBandwidthPrice', { amount: bandwidthLimits.totalPrice }) }}</span>
          <span>{{ $t('routes.bandwidthExceeded.enterpriseResetBandwidthConfirmation') }}</span>
        </v-row>

        <v-row v-if="$canControlTenantConfig()" class="mt-6">
          <v-btn :disabled="isConfirmationSent" @click="sendConfirmation" type="button" class="bg-primary" block>
            {{ $t('common.confirm') }}
          </v-btn>
        </v-row>

        <v-row v-if="!isLogoutHidden" class="mt-5">
          <v-btn :disabled="isConfirmationSent" type="button" @click="logout" class="bg-danger" block>
            {{ $t('layouts.main.logout') }}
          </v-btn>
        </v-row>

        <div v-if="errorMessage" class="bg-error" data-cy="">
          {{ errorMessage }}
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useSystemStore } from '@/stores/SystemStore.js'
import LoginLogo from './components/LoginLogo.vue'
import { useServiceStore } from '@/stores/ServiceStore.js';
import { useTenantStore } from '@/stores/TenantStore.js';

export default {
  name: "BandwidthExceeded",
  components: {
    LoginLogo,
  },
  data: function() {
    return {
      stripeurl: null,
      errorMessage: '',
      tenantLimits: null,
      isConfirmationSent: false,
    };
  },
  mounted: function() {
    if (this.$canControlTenantConfig()) {
      if (this.isStripeSubscription) {
        this.getStripeURL();
      } else if (this.isRecurringSubscription) {
        this.refreshTenantLimits();
      }
    }
  },
  methods: {
    logout: function() {
      this.$router.push('/account/logout');
    },
    getStripeURL: function() {
      //get the portal url
      let self = this;
      const systemStore = useSystemStore();
      systemStore.manageSubscriptionUrl(this.activeAccessPointId)
      .then(function(response) {
        if (response && response.data && response.data.url) {
          self.stripeurl = response.data.url;
        }
      })
      .catch(function(error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          self.errorMessage = error.response.data.error.message;
        } else {
          self.errorMessage = "Error!";
        }
      });
    },
    refreshTenantLimits: async function() {
      const serviceStore = useServiceStore()

      try {
        await serviceStore.refreshLimits({ accessPointId: this.activeAccessPointId })
        this.tenantLimits = serviceStore.getLimits
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = 'Failed to refresh tenant limits.';
        }
      }
    },
    formatBytesAsGigabytes: function (value) {
      return (value / 1000 ** 3).toFixed(2);
    },
    sendConfirmation: async function() {
      const tenantStore = useTenantStore();

      this.isConfirmationSent = true;

      try {
        await tenantStore.resetBandwidth(this.activeAccessPointId);
        this.$router.replace('/');
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = 'Failed to refresh tenant limits.';
        }
      } finally {
        this.isConfirmationSent = false;
      }
    },
  },
  computed: {
    isStripeSubscription: function() {
      const authStore = useAuthStore();
      return authStore.getActiveTenant.subscription.subscriptionType === 'stripe';
    },
    isRecurringSubscription: function() {
      const authStore = useAuthStore();
      const tenantStore = useTenantStore();

      return !tenantStore.getRecurringSubscriptionTypes.includes(
        authStore.getActiveTenant.subscription.subscriptionType
      );
    },
    getUser: function() {
      const authStore = useAuthStore();
      return authStore.user;
    },
    activeAccessPointId: function() {
      const authStore = useAuthStore();
      return authStore.getActiveAccesspoint.node.id
    },
    isLogoutHidden: function() {
      return !this.getUser;
    },
    bandwidthLimits: function() {
      if (this.tenantLimits) {
        return this.tenantLimits.find(l => l.name === 'bandwidth')
      }

      return null;
    }
  }
}
</script>
